import React from "react";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import RemoveIcon from "@mui/icons-material/Remove";
import { SvgIconComponent } from "@mui/icons-material";

export type TrendStatus = "improving" | "stable" | "declining";

type Props = {
  /** 3-state status (the trend utils are untyped JS); unknown values fall back
   *  to "stable". Used when `level` is not supplied. */
  status?: string;
  /** Graded signed level -3 (strong improvement) … +3 (sharp decline). When
   *  provided, takes precedence over `status` and shows intensity via color,
   *  icon weight (single vs. double chevron), and size. */
  level?: number;
  /** Show the text label next to the icon (default true). */
  showLabel?: boolean;
  size?: number;
  className?: string;
};

type Entry = {
  icon: SvgIconComponent;
  color: string;
  label: string;
  /** Icon size multiplier so slight levels read smaller than sharp ones. */
  scale?: number;
};

const statusConfig: Record<TrendStatus, Entry> = {
  declining: { icon: KeyboardArrowDownIcon, color: "text-alert-critical", label: "Declining" },
  improving: { icon: KeyboardArrowUpIcon, color: "text-alert-success", label: "Improving" },
  stable: { icon: RemoveIcon, color: "text-ink-3", label: "Stable" },
};

/** Graded -3..+3 config. Magnitude reads three ways: chevron weight (single →
 *  double), icon size, and color depth (amber → red → deep-red worsening; greens
 *  improving; muted steady). */
const levelConfig: Record<number, Entry> = {
  [-3]: { icon: KeyboardDoubleArrowUpIcon, color: "text-alert-success", label: "Recovering", scale: 1.15 },
  [-2]: { icon: KeyboardArrowUpIcon, color: "text-alert-success", label: "Improving" },
  [-1]: { icon: KeyboardArrowUpIcon, color: "text-alert-success/80", label: "Easing", scale: 0.78 },
  0: { icon: RemoveIcon, color: "text-ink-3", label: "Steady", scale: 0.85 },
  1: { icon: KeyboardArrowDownIcon, color: "text-alert-warning", label: "Slipping", scale: 0.78 },
  2: { icon: KeyboardArrowDownIcon, color: "text-alert-fire", label: "Worsening" },
  3: { icon: KeyboardDoubleArrowDownIcon, color: "text-alert-critical", label: "Deteriorating", scale: 1.15 },
};

/**
 * Trend indicator. Pass `level` for the graded 7-level view (intensity via
 * chevron weight + size + color, e.g. deep-red double-chevron "Deteriorating"),
 * or `status` for the legacy 3-state view. A colored icon plus optional label.
 */
export default function TrendBadge({
  status,
  level,
  showLabel = true,
  size = 20,
  className = "",
}: Props) {
  const entry: Entry =
    level != null
      ? levelConfig[Math.max(-3, Math.min(3, Math.round(level)))] ?? levelConfig[0]
      : statusConfig[(status as TrendStatus)] ?? statusConfig.stable;
  const { icon: Icon, color, label, scale = 1 } = entry;
  return (
    <span
      className={`inline-flex items-center justify-center gap-1.5 font-semibold ${color} ${className}`}
      title={`Safety trend: ${label}`}
    >
      <Icon sx={{ fontSize: size * scale }} />
      {showLabel ? <span>{label}</span> : null}
    </span>
  );
}
